import React from 'react';

import { Tag, Typography } from 'antd';
import styled from 'styled-components';

import { RecommendationInsightClaimType, RecommendationWebEvidenceRunType } from 'types/recommendations';

import { getEvidencePageTitle, humanizeEvidenceValue } from './helpers';
import RecommendationEvidenceArtifactLink from './RecommendationEvidenceArtifactLink';
import { RecommendationEvidenceDetailsPreviewType } from './RecommendationEvidenceDetailsModal';

const ClaimsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 16px;
  padding-right: 8px;
`;

const ClaimItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0px;
  padding: 16px;
  border: 1px solid var(--line-gray);
  border-radius: 4px;
  background: var(--white);
`;

const ClaimHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const ClaimTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const EvidenceActions = styled.div`
  display: flex;
  justify-content: flex-end;

  a {
    font-size: 12px;
  }
`;

const ScreenshotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const getRunCoverage = (evidenceRun: RecommendationWebEvidenceRunType) => {
  const summary = evidenceRun.summary_data;
  return {
    submitted_url: summary.submitted_url,
    pages_discovered: summary.pages_discovered,
    pages_rendered: summary.pages_rendered,
    candidate_safety_limit_reached: summary.candidate_safety_limit_reached,
    time_limit_reached: summary.time_limit_reached,
    access_blocked: summary.access_blocked,
    access_blocked_reason: summary.access_blocked_reason,
    elapsed_seconds: summary.elapsed_seconds,
    source_status: summary.source_status,
    coverage: summary.coverage,
    features: summary.features,
  };
};

type ClaimEvidenceDetails = {
  key: string;
  label: string;
  value: Record<string, unknown>;
};

const getClaimEvidenceDetails = (
  reference: string,
  evidenceRuns: RecommendationWebEvidenceRunType[]
): ClaimEvidenceDetails => {
  const [referenceType, rawId] = reference.split(':');
  const evidenceId = Number(rawId);
  if (referenceType === 'website-page') {
    const page = evidenceRuns.flatMap((evidenceRun) => evidenceRun.pages).find((item) => item.id === evidenceId);
    return page
      ? {
          key: reference,
          label: getEvidencePageTitle(page.extracted_data),
          value: {
            source_url: page.source_url,
            page_type: page.page_type,
            ...page.extracted_data,
          },
        }
      : { key: reference, label: 'Unavailable evidence', value: { reference, available: false } };
  }
  if (referenceType === 'website-run') {
    const evidenceRun = evidenceRuns.find((item) => item.id === evidenceId);
    return evidenceRun
      ? {
          key: reference,
          label: `${evidenceRun.community_name} coverage details`,
          value: getRunCoverage(evidenceRun),
        }
      : { key: reference, label: 'Unavailable coverage', value: { reference, available: false } };
  }
  if (referenceType === 'website-analysis') {
    const evidenceRun = evidenceRuns.find((item) => item.id === evidenceId);
    const modelEvidence = evidenceRun?.summary_data.model_evidence;
    return evidenceRun && modelEvidence
      ? {
          key: reference,
          label: `${evidenceRun.community_name} analysis details`,
          value: modelEvidence,
        }
      : { key: reference, label: 'Unavailable analysis', value: { reference, available: false } };
  }
  return { key: reference, label: 'Unavailable evidence', value: { reference, available: false } };
};

const getClaimScreenshotPages = (
  claim: RecommendationInsightClaimType,
  evidenceRuns: RecommendationWebEvidenceRunType[]
) => {
  const referencedPageIds = new Set(
    claim.evidence_references
      .filter((reference) => reference.startsWith('website-page:'))
      .map((reference) => Number(reference.split(':')[1]))
  );
  return evidenceRuns
    .flatMap((evidenceRun) => evidenceRun.pages)
    .filter((page) => referencedPageIds.has(page.id) && page.artifact);
};

type Props = {
  claims: RecommendationInsightClaimType[];
  evidenceRuns: RecommendationWebEvidenceRunType[];
  onOpenDetails: (preview: RecommendationEvidenceDetailsPreviewType) => void;
};

const RecommendationEvidenceClaims = ({ claims, evidenceRuns, onOpenDetails }: Props) => (
  <ClaimsList>
    {claims.map((claim) => {
      const screenshotPages = getClaimScreenshotPages(claim, evidenceRuns);
      const evidenceTabs = claim.evidence_references.map((reference) =>
        getClaimEvidenceDetails(reference, evidenceRuns)
      );
      return (
        <ClaimItem key={claim.claim_id}>
          <ClaimHeader>
            <Typography.Text strong>{claim.statement}</Typography.Text>
            <ClaimTags>
              <Tag>{humanizeEvidenceValue(claim.claim_type)}</Tag>
              <Tag>{humanizeEvidenceValue(claim.status)}</Tag>
            </ClaimTags>
          </ClaimHeader>
          <Typography.Text type="secondary">{claim.evidence_basis}</Typography.Text>
          <EvidenceActions>
            <Typography.Link
              href="#"
              data-test-id={`aici-website-evidence-claim-details-${claim.claim_id}`}
              onClick={(event) => {
                event.preventDefault();
                onOpenDetails({
                  title: `${humanizeEvidenceValue(claim.section)} claim details`,
                  tabs: [
                    {
                      key: 'claim',
                      label: 'Claim',
                      value: {
                        statement: claim.statement,
                        evidence_basis: claim.evidence_basis,
                        claim_type: claim.claim_type,
                        status: claim.status,
                        output_paths: claim.output_paths,
                      },
                    },
                    ...evidenceTabs,
                  ],
                });
              }}
            >
              Details
            </Typography.Link>
          </EvidenceActions>
          {screenshotPages.length > 0 && (
            <ScreenshotRow>
              {screenshotPages.map((page) => (
                <RecommendationEvidenceArtifactLink
                  key={page.id}
                  artifactUrl={page.artifact || ''}
                  label={`Screenshot of ${getEvidencePageTitle(page.extracted_data)}`}
                  dataTestId={`aici-website-evidence-claim-screenshot-${claim.claim_id}-${page.id}`}
                  thumbnail
                />
              ))}
            </ScreenshotRow>
          )}
        </ClaimItem>
      );
    })}
  </ClaimsList>
);

export default RecommendationEvidenceClaims;
